import type {
  ExecutionMapActivity,
  ExecutionMapLayerVisibility,
  ExecutionMapOverlayModel,
  ExecutionMapPointState,
} from '../map/executionMapTypes';
import type { ExecutionActivityViewModel, ExecutionTripViewModel } from './executionTypes';

export const DEFAULT_EXECUTION_MAP_LAYERS: ExecutionMapLayerVisibility = {
  plannedRoutes: true,
  actualRoutes: true,
  points: true,
  sequences: true,
  vehicles: true,
  exceptions: true,
};

const getPointState = (
  activity: ExecutionActivityViewModel,
  currentActivityId: string | null,
  nextActivityId: string | null
): ExecutionMapPointState => {
  if (activity.kind === 'UNPLANNED_STOP') return 'unplanned';
  if (activity.id === currentActivityId) return 'current';
  if (activity.id === nextActivityId) return 'next';
  if (activity.outcome === 'RTO') return 'rto';
  if (
    activity.actualSequence != null &&
    activity.plannedSequence != null &&
    activity.actualSequence !== activity.plannedSequence
  ) return 'out-of-sequence';
  if (activity.actualSequence != null) return 'visited';
  return 'planned';
};

const toMapActivity = (
  activity: ExecutionActivityViewModel,
  currentActivityId: string | null,
  nextActivityId: string | null
): ExecutionMapActivity => ({
  id: activity.id,
  title: activity.title,
  location: activity.location,
  plannedSequence: activity.plannedSequence,
  actualSequence: activity.actualSequence,
  state: getPointState(activity, currentActivityId, nextActivityId),
  exceptionTags: activity.exceptionTags,
});

export const buildExecutionMapOverlay = (
  trips: ExecutionTripViewModel[],
  visibleTripIds: string[],
  activeTripId: string | null,
  layers: ExecutionMapLayerVisibility,
  activeActivityId: string | null
): ExecutionMapOverlayModel => ({
  trips: trips
    .filter((trip) => visibleTripIds.includes(trip.id))
    .map((trip) => {
      const nextActivity = trip.activities.find((activity) => (
        activity.kind === 'TOUCH_POINT' && activity.outcome === 'PENDING'
      ));
      const currentActivityId = trip.phase === 'ACTIVE' || trip.phase === 'DELAYED'
        ? trip.currentActivityId
        : null;
      return {
        id: trip.id,
        color: trip.color,
        active: trip.id === activeTripId,
        plannedRoute: trip.route.planned,
        actualRoute: trip.route.actual,
        vehiclePosition: trip.vehiclePosition,
        activities: trip.activities.map((activity) => (
          toMapActivity(activity, currentActivityId, nextActivity?.id ?? null)
        )),
      };
    }),
  layers,
  activeActivityId,
});
